import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { supabase } from "../../../integrations/supabase/client";
import { Button } from "../../../components/ui/button";

export const Route = createFileRoute("/auth/confirm/")({
  component: ConfirmEmailPage,
});

function ConfirmEmailPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (typeof window === "undefined" || !supabase) return;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace("#", "?"));
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    const type = params.get("type");

    if (accessToken && refreshToken && type === "signup") {
      supabase.auth
        .setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(({ error }) => {
          if (error) {
            setStatus("error");
            setMessage(error.message);
          } else {
            setStatus("success");
            setMessage("Email confirmed successfully! Redirecting...");
            setTimeout(() => navigate({ to: "/dashboard" }), 2000);
          }
        });
    } else if (type === "email_change" || type === "magiclink") {
      if (accessToken && refreshToken) {
        supabase.auth
          .setSession({ access_token: accessToken, refresh_token: refreshToken })
          .then(({ error }) => {
            if (error) {
              setStatus("error");
              setMessage(error.message);
            } else {
              setStatus("success");
              setMessage("Verified! Redirecting...");
              setTimeout(() => navigate({ to: "/dashboard" }), 2000);
            }
          });
      } else {
        setStatus("error");
        setMessage("Invalid or expired confirmation link.");
      }
    } else {
      setStatus("error");
      setMessage("Invalid confirmation link. Please check your email and try again.");
    }
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6 text-center">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          Email Confirmation
        </h1>

        {status === "loading" && (
          <p className="text-muted-foreground">Verifying your email...</p>
        )}

        {status === "success" && (
          <div className="space-y-4">
            <div className="text-4xl">✅</div>
            <p className="text-green-600 dark:text-green-400">{message}</p>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-4">
            <div className="text-4xl">❌</div>
            <p className="text-destructive">{message}</p>
            <Button variant="outline" asChild>
              <Link to="/auth">Back to Sign In</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
